import { Chip } from '@/components/commons/chip'
import { Avatar } from '@/components/ui/avatar'
import { AlertTriangleIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { useAuthStore } from '@/features/auth/store/store.ts'
import {
	OpsVettingLabel,
	OpsVettingParagraph,
	OpsVettingQuestionsGrid,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { VettingSectionStatusSelector } from '@/features/ops-vetting/components/vetting-section-status-selector'
import { ServiceVettingCriteriaFlags } from '@/features/service-vetting/components/service-vetting-criteria-flags'
import { useAddServiceExpertiseAnswerFlags } from '@/features/service-vetting/query/use-add-service-expertise-answer-flags.ts'
import { useAddServiceExpertiseFlags } from '@/features/service-vetting/query/use-add-service-expertise-flags.ts'
import { useRemoveServiceExpertiseAnswerFlags } from '@/features/service-vetting/query/use-remove-service-expertise-answer-flags.ts'
import { useRemoveServiceExpertiseFlags } from '@/features/service-vetting/query/use-remove-service-expertise-flags.ts'
import { useUpdateServiceExpertiseStatus } from '@/features/service-vetting/query/use-update-service-expertise-status.ts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import type { RemappedService } from '@/features/service-vetting/types.ts'
import { groupByCountry } from '@/features/service-vetting/utils.ts'
import { objectEntries } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'

export const ServiceVettingCriteriaHostExpertise = ({
	service
}: {
	service: RemappedService
}) => {
	const { t } = useTranslation()
	const updateExpertiseStatus = useUpdateServiceExpertiseStatus({
		serviceId: service.id,
		expertiseId: service.expertise.id
	})
	const errors = useServiceVettingError((state) => state.errors)
	const addExpertiseFlags = useAddServiceExpertiseFlags({
		serviceId: service.id,
		itemId: service.expertise.id
	})
	const removeExpertiseFlags = useRemoveServiceExpertiseFlags({
		serviceId: service.id,
		itemId: service.expertise.id
	})
	const user = useAuthStore((state) => state.user)

	const addExpertiseQuestionFlags = useAddServiceExpertiseAnswerFlags({
		serviceId: service.id,
		sectionId: service.expertise.id
	})
	const removeExpertiseQuestionFlags = useRemoveServiceExpertiseAnswerFlags({
		serviceId: service.id,
		sectionId: service.expertise.id
	})

	const groupedCities = groupByCountry(service.submission_cities)

	const isDeclined = service.expertise.status === 'declined'
	const isBackToDraft = service.expertise.status === 'back_to_draft'
	const hideAction =
		service.status !== 'under_review' ||
		user?.role === 'read_only' ||
		service.is_host_modified
	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<div className='flex shrink-0 items-center gap-8'>
					<OpsVettingSectionTitle>
						{t('vetting:criteria.service.expertise.title')}
					</OpsVettingSectionTitle>
					<VettingSectionStatusSelector
						disabled={hideAction}
						onChange={(status) =>
							updateExpertiseStatus.mutate({ status })
						}
						value={service.expertise.status}
					/>
				</div>
				<div className='flex items-center gap-4'>
					{errors.expertiseErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
					{isDeclined && (
						<ServiceVettingCriteriaFlags
							addFlag={(flag) =>
								addExpertiseFlags.mutate({ flags: [flag] })
							}
							badgeVariant={'danger'}
							flagPosition={'left'}
							flags={service.expertise.flags}
							hideAction={hideAction}
							removeFlag={(flag) =>
								removeExpertiseFlags.mutate({ flags: [flag] })
							}
							types={['decline_expertise']}
						/>
					)}
				</div>
			</div>
			<Separator orientation='horizontal' />
			<OpsVettingQuestionsGrid>
				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.service.expertise.experience_years'
							)}
						</OpsVettingLabel>
						<OpsVettingParagraph>
							{
								service.expertise.question_answer
									.experience_years.answer
							}
						</OpsVettingParagraph>
						{isBackToDraft && (
							<ServiceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: service.expertise
											.question_answer.experience_years.id
									})
								}
								flags={
									service.expertise.question_answer
										.experience_years.flags
								}
								hideAction={hideAction}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: service.expertise
											.question_answer.experience_years.id
									})
								}
								types={['host_expertise']}
							/>
						)}
					</div>

					<div className='flex w-full flex-col gap-6'>
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t(
									'vetting:criteria.service.expertise.summary'
								)}
							</OpsVettingLabel>
							<OpsVettingParagraph>
								{
									service.expertise.question_answer.summary
										.answer
								}
							</OpsVettingParagraph>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.summary.id
										})
									}
									flags={
										service.expertise.question_answer
											.summary.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.summary.id
										})
									}
									types={['general', 'host_expertise']}
								/>
							)}
						</div>
					</div>
					<div className='flex w-full flex-col gap-6'>
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t(
									'vetting:criteria.service.expertise.highlights'
								)}
							</OpsVettingLabel>
							<OpsVettingParagraph>
								{
									service.expertise.question_answer.highlights
										.answer
								}
							</OpsVettingParagraph>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.highlights.id
										})
									}
									flags={
										service.expertise.question_answer
											.highlights.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.highlights.id
										})
									}
									types={['general', 'host_expertise']}
								/>
							)}
						</div>
					</div>
					<div className='flex w-full flex-col gap-6'>
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t(
									'vetting:criteria.service.expertise.listing_title'
								)}
							</OpsVettingLabel>
							<OpsVettingParagraph>
								{
									service.expertise.question_answer
										.inspiration.answer
								}
							</OpsVettingParagraph>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.inspiration.id
										})
									}
									flags={
										service.expertise.question_answer
											.inspiration.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.inspiration.id
										})
									}
									types={[
										'general',
										'title',
										'host_expertise'
									]}
								/>
							)}
						</div>
					</div>
					<div className='flex w-full flex-col gap-6'>
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t(
									'vetting:criteria.service.expertise.background'
								)}
							</OpsVettingLabel>
							<OpsVettingParagraph>
								{
									service.expertise.question_answer.background
										.answer
								}
							</OpsVettingParagraph>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.background.id
										})
									}
									flags={
										service.expertise.question_answer
											.background.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.background.id
										})
									}
									types={['general', 'host_expertise']}
								/>
							)}
						</div>
					</div>
				</div>

				<Separator orientation='vertical' />

				<div className='flex w-full flex-col gap-6'>
					<div className='flex flex-col gap-2'>
						<OpsVettingLabel>
							{t(
								'vetting:criteria.service.expertise.profile_picture'
							)}
						</OpsVettingLabel>
						<div className='grid grid-cols-[auto_1fr] items-center gap-3'>
							<Avatar
								imageUrl={service.host.profile_photo_url || ''}
								name={service.host.first_name ?? ''}
								size={'lg'}
							/>
							<p className='text-neutral-900 text-sm'>
								{service.host.first_name}{' '}
								{service.host.last_name}
							</p>
						</div>
						{isBackToDraft && (
							<ServiceVettingCriteriaFlags
								addFlag={(flag) =>
									addExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: service.expertise
											.question_answer.profile_photo.id
									})
								}
								flags={
									service.expertise.question_answer
										.profile_photo.flags
								}
								hideAction={hideAction}
								removeFlag={(flag) =>
									removeExpertiseQuestionFlags.mutate({
										flags: [flag],
										itemId: service.expertise
											.question_answer.profile_photo.id
									})
								}
								types={['profile_photo']}
							/>
						)}
					</div>

					{service.expertise.question_answer.links.answer.length >
						0 && (
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t('vetting:criteria.service.expertise.links')}
							</OpsVettingLabel>
							<ol className='list-decimal pl-4 [&>li>a]:cursor-pointer [&>li>a]:text-sm [&>li>a]:underline'>
								{service.expertise.question_answer.links.answer.map(
									(link) => (
										<li key={link}>
											<a href={link} target='_blank'>
												<OpsVettingParagraph>
													{link}
												</OpsVettingParagraph>
											</a>
										</li>
									)
								)}
							</ol>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.links.id
										})
									}
									flags={
										service.expertise.question_answer.links
											.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.links.id
										})
									}
									types={['host_expertise']}
								/>
							)}
						</div>
					)}

					{service.expertise.question_answer?.location?.answer && (
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t(
									'vetting:criteria.service.expertise.location'
								)}
							</OpsVettingLabel>
							<OpsVettingParagraph>
								{
									service.expertise.question_answer.location
										.answer
								}
							</OpsVettingParagraph>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										service.expertise.question_answer
											.location?.id &&
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.location.id
										})
									}
									flags={
										service.expertise.question_answer
											.location.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										service.expertise.question_answer
											.location?.id &&
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.location.id
										})
									}
									types={['location']}
								/>
							)}
						</div>
					)}

					{(service.submission_cities.length > 0 ||
						service.submission_places.length > 0) && (
						<div className='flex flex-col gap-2'>
							<OpsVettingLabel>
								{t('vetting:criteria.service.expertise.areas')}
							</OpsVettingLabel>
							<ol className='list-decimal pl-4 [&>li>a]:cursor-pointer [&>li>a]:text-sm [&>li>a]:underline'>
								{service.submission_places.length > 0
									? service.submission_places.map((place) => (
											<li key={place.place.name}>
												<OpsVettingParagraph>
													{place.place.name}
												</OpsVettingParagraph>
											</li>
										))
									: objectEntries(groupedCities).map(
											([country, cities]) => (
												<li key={country}>
													<OpsVettingParagraph>{`${country}: ${cities.join(
														', '
													)}`}</OpsVettingParagraph>
												</li>
											)
										)}
							</ol>
							{isBackToDraft && (
								<ServiceVettingCriteriaFlags
									addFlag={(flag) =>
										addExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.cities.id
										})
									}
									flags={
										service.expertise.question_answer.cities
											.flags
									}
									hideAction={hideAction}
									removeFlag={(flag) =>
										removeExpertiseQuestionFlags.mutate({
											flags: [flag],
											itemId: service.expertise
												.question_answer.cities.id
										})
									}
									types={['location']}
								/>
							)}
						</div>
					)}
				</div>
			</OpsVettingQuestionsGrid>
		</OpsVettingSectionWrapper>
	)
}
